import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TransparentIconButton } from "~assets/frontend/elements/button/Buttons";
import { SecondaryButtonLink } from "~assets/frontend/elements/button-link/ButtonLink";
import { TextLink } from "~assets/frontend/elements/text-link/TextLink";
import { useBreakpoint } from "~assets/frontend/_util/hooks/useBreakpoint";
import { clamp } from "~assets/frontend/_util/clamp";
import { useSwipe } from "~assets/frontend/_util/hooks/useSwipe";
import Translator from "bazinga-translator";
import { dispatchTrackingEvent } from "~assets/global/tracking/tracking-event";
import { BREAKPOINT } from "~assets/frontend/_util/breakpoints";
import { getId } from "~assets/frontend/_util/getId";
export function calculateStepSize(breakpoint, stepSize) {
    // If stepsize is a number, it's a fixed value in all breakpoints
    if (stepSize && typeof stepSize === "number") {
        return stepSize;
    }
    // If its an object it depends on the breakpoint
    if (stepSize && typeof stepSize === "object" && "xs" in stepSize) {
        // Determine responsive breakpoint with fallback
        if (typeof breakpoint === "number") {
            return stepSize.xs;
        }
        let newStepSize = 1;
        if (breakpoint === BREAKPOINT.XL) {
            newStepSize = -1; // aka use next available size
            if (BREAKPOINT.XL in stepSize && typeof stepSize.xl === "number") {
                newStepSize = stepSize.xl;
            }
        }
        if (breakpoint === BREAKPOINT.LG || newStepSize < 1) {
            newStepSize = -1; // aka use next available size
            if (BREAKPOINT.LG in stepSize && typeof stepSize.lg === "number") {
                newStepSize = stepSize.lg;
            }
        }
        if (breakpoint === BREAKPOINT.MD || newStepSize < 1) {
            newStepSize = -1; // aka use next available size
            if (BREAKPOINT.MD in stepSize && typeof stepSize.md === "number") {
                newStepSize = stepSize.md;
            }
        }
        if (breakpoint === BREAKPOINT.SM || newStepSize < 1) {
            newStepSize = -1; // aka use next available size
            if (BREAKPOINT.SM in stepSize && typeof stepSize.sm === "number") {
                newStepSize = stepSize.sm;
            }
        }
        if (newStepSize < 1) {
            newStepSize = stepSize.xs;
        }
        return newStepSize;
    }
    return 1;
}
export const Carousel = ({ externalContainer, children, ctaLabel, ctaType, ctaHref, stepSize }) => {
    // Determine the child element; if there is no child element, the external container is used
    let reactContainer = null;
    if (children) {
        reactContainer = children;
        if (reactContainer.type !== "div") {
            throw new Error("Carousel children must be a div");
        }
        if (!reactContainer.props.children || reactContainer.props.children.length === 0) {
            throw new Error("Carousel children must have children");
        }
    }
    const childWrapperRef = useRef(null);
    const [index, setIndex] = useState(0);
    const breakpoint = useBreakpoint();
    // Handle focus change on child elements
    useEffect(() => {
        if (childWrapperRef.current) {
            // You can't just add a focus listener to the children React components because they are not html elements...
            // So the workaround is to add a focus listener to the child wrapper and then determine which child is focused
            childWrapperRef.current.addEventListener("focusin", (event) => {
                if (event.target instanceof HTMLElement) {
                    let currentElement = event.target;
                    let previousElement = null;
                    let previousPreviousElement = null;
                    while (currentElement.parentElement !== null && currentElement !== childWrapperRef.current) {
                        if (previousElement) {
                            previousPreviousElement = previousElement;
                        }
                        if (currentElement) {
                            previousElement = currentElement;
                        }
                        currentElement = currentElement.parentElement;
                    }
                    if (previousElement && previousPreviousElement) {
                        const focusedIndex = Array.from(previousElement.children).indexOf(previousPreviousElement);
                        setIndex(focusedIndex);
                    }
                }
            });
        }
        else if (externalContainer) {
            Array.from(externalContainer.children).forEach((child, index) => {
                child.addEventListener("focusin", (event) => {
                    setIndex(index);
                    event.stopImmediatePropagation();
                });
            });
        }
    }, [childWrapperRef, externalContainer]);
    // Determine the total number of slides
    const [total, setTotal] = useState(0);
    useEffect(() => {
        if (reactContainer) {
            setTotal(reactContainer.props.children.length);
        }
        else if (externalContainer) {
            setTotal(externalContainer.children.length);
        }
        else {
            throw new Error("Carousel must have children or an external container");
        }
    }, [reactContainer, externalContainer]);
    // Only allow sliding when the content is overflowing the container
    const isSlidingEnabledFn = useCallback(() => {
        let element = null;
        if (childWrapperRef.current) {
            element = childWrapperRef.current.children[0];
        }
        else if (externalContainer) {
            element = externalContainer;
        }
        if (element) {
            const elementStyle = getComputedStyle(element);
            const elementWidth = element.getBoundingClientRect().width - parseInt(elementStyle.paddingLeft) - parseInt(elementStyle.paddingRight);
            const contentWidth = element.scrollWidth;
            const enableSliding = contentWidth > elementWidth;
            if (!enableSliding && index > 0) {
                element.style.transition = "transform 0s";
                element.style.transform = `translateX(0)`;
            }
            return enableSliding;
        }
        return false;
    }, [breakpoint, externalContainer, childWrapperRef]);
    const isSlidingEnabled = isSlidingEnabledFn();
    // The index stores the current slide and when it changes the carousel is updated
    useEffect(() => {
        if (!isSlidingEnabled) {
            return;
        }
        let element = null;
        if (childWrapperRef.current) {
            element = childWrapperRef.current.children[0];
        }
        else if (externalContainer) {
            element = externalContainer;
        }
        if (element) {
            let activeSlide = element.children[index];
            const elementStyle = getComputedStyle(element);
            const elementOffset = element.getBoundingClientRect().left + parseInt(elementStyle.paddingLeft);
            let offset = elementOffset - activeSlide.getBoundingClientRect().left;
            // Get current transform offset
            const currentOffset = element.style.transform ? parseInt(element.style.transform.replace("translateX(", "").replace("px)", "")) : 0;
            const delta = currentOffset - offset;
            const duration = Math.abs(delta) / 2;
            element.style.transition = `transform ${duration}ms ease-out`;
            element.style.transform = `translateX(${offset}px)`;
        }
    }, [breakpoint, index, childWrapperRef, isSlidingEnabled]);
    // The prev and next functions are used to update the index
    // How far the function jumps depends on the stepSize
    let currentStepSize = useMemo(() => {
        return calculateStepSize(breakpoint, stepSize);
    }, [stepSize, breakpoint]);
    const onPrev = () => {
        if (!isSlidingEnabled) {
            return;
        }
        const nextIndex = clamp(0, index - currentStepSize, total - 1);
        setIndex(nextIndex);
        dispatchTrackingEvent({
            name: "ui",
            payload: {
                ui_component: "comp-carousel",
                ui_function: "carousel",
                ui_action: "prev",
                ui_target: "Item " + nextIndex,
            },
        });
    };
    const onNext = () => {
        if (!isSlidingEnabled) {
            return;
        }
        const nextIndex = clamp(0, index + currentStepSize, total - 1);
        setIndex(nextIndex);
        dispatchTrackingEvent({
            name: "ui",
            payload: {
                ui_component: "comp-carousel",
                ui_function: "carousel",
                ui_action: "next",
                ui_target: "Item " + nextIndex,
            },
        });
    };
    // Set up swiping
    useSwipe({
        left: onPrev,
        right: onNext,
    }, childWrapperRef.current || externalContainer);
    // Set up the CTA
    let cta = null;
    if (ctaLabel && ctaType && ctaHref) {
        if (ctaType === "button") {
            cta = (React.createElement(SecondaryButtonLink, { _id: "", _name: "ButtonLinkStruct", label: ctaLabel, href: ctaHref, className: "order-last w-full flex-shrink-0 justify-center sm:order-first sm:w-auto" }));
        }
        else {
            cta = React.createElement(TextLink, { _id: "", _name: "TextLinkStruct", label: ctaLabel, href: ctaHref, icon: { identifier: "icon-arrow-right", position: "end" } });
        }
    }
    // Render the carousel
    return (React.createElement(React.Fragment, null,
        children && React.createElement("div", { ref: childWrapperRef }, children),
        (isSlidingEnabled || cta) && (React.createElement("div", { className: "mt-12 flex flex-wrap items-center justify-between gap-6 lg:mt-24" },
            cta,
            React.createElement("div", null),
            isSlidingEnabled && (React.createElement("div", { className: "flex gap-12" },
                React.createElement(TransparentIconButton, { _id: getId(), _name: "IconButtonStruct", label: Translator.trans("comp-carousel.scroll_left", {}, "frontend"), iconIdentifier: "icon-arrow-left", onClick: onPrev, disabled: index === 0 }),
                React.createElement(TransparentIconButton, { _id: getId(), _name: "IconButtonStruct", label: Translator.trans("comp-carousel.scroll_right", {}, "frontend"), iconIdentifier: "icon-arrow-right", onClick: onNext, disabled: index === total - 1 })))))));
};
